import React, { useState, useEffect, useMemo } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { 
    FiCode, FiCopy, FiShield, FiSliders, FiSearch, FiCheckCircle, FiZap, FiLayers, FiEye, 
    FiChevronDown, FiFilter, FiActivity, FiRepeat, FiGrid, FiArrowRight, FiPlus, FiLayout 
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

// --- IMPORTAÇÕES DE TEMPLATES ---
import GiantImperial from '../components/templates/GiantImperial';
import GiantTitan from '../components/templates/GiantTitan';
import GiantCyber from '../components/templates/GiantCyber';
import GiantMinimal from '../components/templates/GiantMinimal';
import GiantMonarch from '../components/templates/GiantMonarch';
import MedGlass from '../components/templates/MedGlass';
import MedIndustrial from '../components/templates/MedIndustrial';
import MedFloating from '../components/templates/MedFloating';
import MedDarkPro from '../components/templates/MedDarkPro';
import MedModern from '../components/templates/MedModern';
import SmallMarket from '../components/templates/SmallMarket';
import SmallListStealth from '../components/templates/SmallListStealth';
import SmallCardNano from '../components/templates/SmallCardNano';
import SmallQuickAction from '../components/templates/SmallQuickAction';
import SmallSearchPro from '../components/templates/SmallSearchPro';
import SmallGoldPublic from '../components/templates/SmallGoldPublic';
import SmallClassicML from '../components/templates/SmallClassicML';
import SmallExpressOLX from '../components/templates/SmallExpressOLX';
import SmallFeatureNano from '../components/templates/SmallFeatureNano';
import SmallGridElite from '../components/templates/SmallGridElite';

const CARD_LIBRARY = [
    { id: 'giant-imperial', name: 'Monólito Imperial', category: 'INICIAL', preview: '🏛️', comp: GiantImperial },
    { id: 'giant-titan', name: 'Split Titan', category: 'INICIAL', preview: '⚡', comp: GiantTitan },
    { id: 'giant-cyber', name: 'Cyber Neon', category: 'INICIAL', preview: '🔮', comp: GiantCyber },
    { id: 'giant-minimal', name: 'Minimal White', category: 'INICIAL', preview: '🏳️', comp: GiantMinimal },
    { id: 'giant-monarch', name: 'Classic Monarch', category: 'INICIAL', preview: '👑', comp: GiantMonarch },
    { id: 'small-grid', name: 'Insta Elite Grid', category: 'INSTAGRAM', preview: '🔳', comp: SmallGridElite },
    { id: 'med-modern', name: 'Modern Grid', category: 'INSTAGRAM', preview: '📱', comp: MedModern },
    { id: 'small-gold', name: 'Gold Public', category: 'INSTAGRAM', preview: '🏆', comp: SmallGoldPublic },
    { id: 'small-market', name: 'Market Nano', category: 'INSTAGRAM', preview: '📟', comp: SmallMarket },
    { id: 'small-feature', name: 'Blog/Ads Banner', category: 'EXTRA', preview: '🏷️', comp: SmallFeatureNano },
    { id: 'small-quick', name: 'Floating Action', category: 'EXTRA', preview: '🔘', comp: SmallQuickAction },
    { id: 'small-stealth', name: 'List Footer', category: 'EXTRA', preview: '☰', comp: SmallListStealth },
    { id: 'small-search', name: 'Maps Mini', category: 'EXTRA', preview: '📍', comp: SmallSearchPro },
];

const MOCK_PROPERTY = {
    titulo: 'Penthouse Platinum Garden',
    bairro: 'Batel', cidade: 'Curitiba', preco: 12450000,
    imagensDestaque: ['https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=2070&auto=format&fit=crop'],
    tipoPrincipal: 'Cobertura de Exceção', isDestaque: true
};

export default function TemplateIntegrationPage() {
    const [config, setConfig] = useState({
        cardId: 'giant-imperial',
        shortcodeColor: '#d4af37',
        filterColor: '#0ea5e9',
        filterStyle: 'glass',
        apiKey: 'mos_live_sniper_v11',
        limit: 10,
        order: 'recentes',
        infiniteScroll: true,
        showFilters: ['busca', 'tipo', 'cidade']
    });

    const shortcode = useMemo(() => {
        return `<div id="mos-root" \n  data-api-key="${config.apiKey}" \n  data-card-id="${config.cardId}"\n  data-infinite="${config.infiniteScroll}"\n  data-filter-style="${config.filterStyle}"\n  data-filter-color="${config.filterColor}"\n  data-color="${config.shortcodeColor}">\n</div>\n<script src="https://mos-imoveis.com.br/sdk.js" defer></script>`;
    }, [config]);

    const SelectedTemplate = useMemo(() => {
        const template = CARD_LIBRARY.find(c => c.id === config.cardId);
        return template ? template.comp : GiantImperial;
    }, [config.cardId]);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shortcode);
        toast.success("Shortcode Sniper copiado!");
    };

    return (
        <MainLayout>
            <div className="w-full min-h-screen space-y-20 max-w-[1750px] mx-auto p-8 md:p-16 transition-all duration-500 pb-60"
                 style={{ backgroundColor: 'var(--bg-main)' }}>
                
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-10 border-b pb-12" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-8">
                        <div className="w-20 h-20 rounded-3xl bg-accent/10 flex items-center justify-center text-accent border border-accent/20 shadow-2xl">
                            <FiZap size={32} className="animate-pulse" />
                        </div>
                        <div>
                            <h1 className="text-5xl font-black tracking-tighter uppercase text-auto">Sniper <span className="text-accent">Orchestrator</span></h1>
                            <p className="text-[10px] font-black uppercase tracking-[0.4em] opacity-40">Protocolo de Injeção V12</p>
                        </div>
                    </div>
                </header>

                {/* ETAPA 1: BIBLIOTECA */}
                <section className="space-y-12">
                    <h2 className="text-3xl font-black uppercase tracking-tighter text-auto">01 - <span className="text-accent">Biblioteca de Ativos</span></h2>
                    {['INICIAL', 'INSTAGRAM', 'EXTRA'].map(cat => (
                        <div key={cat} className="space-y-6">
                            <h3 className="text-[11px] font-black uppercase opacity-30 flex items-center gap-4">
                                <FiLayout /> {cat === 'INICIAL' ? 'Home Page (Sessão Única)' : cat === 'INSTAGRAM' ? 'Instagram Style (Grid)' : 'Shortcodes Extras'}
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                                {CARD_LIBRARY.filter(c => c.category === cat).map((card) => (
                                    <button key={card.id} onClick={() => setConfig({...config, cardId: card.id})}
                                            className={`p-8 rounded-[2.5rem] border transition-all duration-500 flex flex-col items-center gap-4 group relative ${config.cardId === card.id ? 'border-accent bg-accent/5 shadow-2xl scale-105' : 'border-auto opacity-40 hover:opacity-100 hover:bg-card-auto'}`}>
                                        <span className="text-4xl group-hover:scale-110 transition-transform">{card.preview}</span>
                                        <div className="text-center text-[10px] font-black uppercase tracking-tighter text-auto truncate w-full">{card.name}</div>
                                        {config.cardId === card.id && <FiCheckCircle className="absolute top-4 right-4 text-accent" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </section>

                {/* ETAPA 1.5: FILTROS */}
                <section className="space-y-10">
                    <h2 className="text-3xl font-black uppercase tracking-tighter text-auto">1.5 - <span className="text-accent">Filtro Tático</span></h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="p-10 rounded-[3rem] bg-card-auto border border-auto space-y-6 shadow-2xl">
                            <h3 className="text-xs font-black uppercase flex items-center gap-3 text-accent"><FiSliders /> Estilo da Barra</h3>
                            <div className="grid grid-cols-3 gap-3">
                                {['minimal', 'glass', 'industrial'].map(s => (
                                    <button key={s} onClick={() => setConfig({...config, filterStyle: s})}
                                            className={`p-4 rounded-xl text-[9px] font-black uppercase border transition-all ${config.filterStyle === s ? 'bg-accent text-black' : 'bg-transparent opacity-40'}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <input type="color" value={config.filterColor} onChange={(e) => setConfig({...config, filterColor: e.target.value})} className="w-full h-12 rounded-xl mt-4 cursor-pointer bg-transparent border-none" />
                        </div>
                        <div className="p-10 rounded-[3rem] bg-card-auto border border-auto space-y-6 shadow-2xl text-auto">
                            <h3 className="text-xs font-black uppercase flex items-center gap-3 text-accent"><FiFilter /> Ordem do Radar</h3>
                            <div className="grid grid-cols-2 gap-2">
                                {['recentes', 'preco_max', 'preco_min', 'nome'].map(o => (
                                    <button key={o} onClick={() => setConfig({...config, order: o})} className={`p-4 rounded-xl text-[9px] font-black uppercase border ${config.order === o ? 'bg-accent text-black' : 'opacity-40'}`}>{o}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ETAPA 2: VISION MODE */}
                <section className="space-y-10">
                    <h2 className="text-3xl font-black uppercase tracking-tighter text-auto">02 - <span className="text-accent">Ver Site Como Ficará</span></h2>
                    <div className="rounded-[4.5rem] bg-white border border-gray-100 shadow-2xl overflow-hidden min-h-[1500px] flex flex-col">
                        <div className="h-[750px] w-full overflow-hidden border-b border-gray-100 bg-black flex items-center justify-center relative">
                             <SelectedTemplate data={MOCK_PROPERTY} color={config.shortcodeColor} />
                        </div>

                        <div className="sticky top-0 z-20 p-8 flex justify-center bg-white/80 backdrop-blur-xl border-b border-gray-100">
                            <div className="container flex items-center gap-4">
                                {config.showFilters.map(f => (
                                    <div key={f} className="px-8 py-5 rounded-2xl text-[10px] font-black uppercase border flex items-center gap-4 shadow-sm" style={{ borderColor: config.filterStyle === 'industrial' ? config.filterColor : '#eee', color: config.filterStyle === 'industrial' ? config.filterColor : 'black' }}>
                                        {f} <FiChevronDown />
                                    </div>
                                ))}
                                <div className="flex-1" />
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white" style={{ backgroundColor: config.filterColor }}><FiSearch /></div>
                            </div>
                        </div>

                        <div className="p-16 bg-[#fafafa] flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                                {[...Array(15)].map((_, i) => (
                                    <div key={i}><SmallGridElite data={{...MOCK_PROPERTY, titulo: `Ativo Premium ${i+1}`}} color={config.shortcodeColor} /></div>
                                ))}
                            </div>
                            <div className="mt-24 text-center">
                                <button className="px-20 py-8 rounded-full bg-black text-white font-black uppercase text-[11px] tracking-[0.6em] shadow-2xl">Explorar Mais <FiRepeat className="inline ml-4" /></button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ETAPA 3: EXPORT */}
                <section className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4 p-10 rounded-[3rem] bg-card-auto border border-auto space-y-8 shadow-2xl">
                        <h3 className="text-xs font-black uppercase flex items-center gap-3 text-accent"><FiSliders /> Cor dos Cards</h3>
                        <input type="color" value={config.shortcodeColor} onChange={(e) => setConfig({...config, shortcodeColor: e.target.value})} className="w-full h-12 rounded-xl cursor-pointer" />
                    </div>

                    <div className="lg:col-span-8 p-12 rounded-[4rem] bg-accent text-black space-y-8 shadow-2xl relative overflow-hidden group">
                        <FiCode size={300} className="absolute -right-20 -bottom-20 opacity-10" />
                        <div className="flex justify-between items-center relative z-10">
                            <h3 className="text-lg font-black uppercase tracking-widest flex items-center gap-3"><FiCode /> Script Gerado</h3>
                        </div>
                        <div className="bg-black/90 p-10 rounded-[2.5rem] border border-white/10 shadow-inner relative z-10 overflow-hidden">
                            <pre className="text-xs font-mono text-accent whitespace-pre-wrap">{shortcode}</pre>
                        </div>
                        <button onClick={copyToClipboard} className="w-full py-8 rounded-3xl bg-black text-white font-black uppercase text-sm tracking-[0.4em] relative z-10 flex items-center justify-center gap-4">
                            <FiCopy size={24} /> Copiar Shortcode sniper
                        </button>
                    </div>
                </section>

                <footer className="text-center opacity-20 py-20">
                    <p className="text-[10px] font-black uppercase tracking-[2em]">MOS GOLD ENGINE // V12</p>
                </footer>
            </div>
        </MainLayout>
    );
}